// Gemini Live's output transcription of the voice agent is posted to the support ticket
// as a visible AiFrontendAgent comment, so the customer and the concierge read it as text.
// Speech artifacts that pass out loud (or are not even spoken) must not reach the ticket.

// Non-speech annotations like "[Silence]" and our own injected markers. Gemini never
// legitimately speaks square brackets. An unterminated one is cut at the end of the text.
const BRACKETED_TOKEN = /\[[^\]]*(?:\]|$)/g;

// The session-start greeting. Repeated on every new voice session, so on a ticket it shows
// up as "Hey, this is Tidy. How can I help?" in the middle of an ongoing conversation.
const GREETING = /\b(?:hey|hi|hello)\b[,!.]?\s*(?:there[,!.]?\s*)?(?:this is|it['’]s)\s+tidy\b[^.!?]*[.!?]?(?:\s*how can i help\b[^.!?]*[.!?]?)?/gi;

// Everything up to the last terminal punctuation; what follows was cut off mid-sentence.
const UP_TO_LAST_SENTENCE_END = /^[\s\S]*[.!?…]["'’”)]*/;

// Only split where a new sentence clearly starts, so "2:30 p.m. Got it." stays intact.
const SENTENCE_BOUNDARY = /(?<=[.!?…]["'’”)]*)\s+(?=[A-Z])/;

function normalize(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * Returns the text worth posting to the ticket for one voice-agent turn, or null when nothing
 * is left. Drops placeholders, the greeting, a trailing unfinished sentence, a turn that starts
 * mid-sentence, repeated sentences, and a turn identical to the previous posted one.
 */
export function cleanVoiceTranscript(raw: string, previousPosted: string | null = null): string | null {
  const text = raw
    .replace(BRACKETED_TOKEN, ' ')
    .replace(GREETING, ' ')
    // Consecutive turns get concatenated without a space ("shortly.I'm handing").
    .replace(/([.!?])(?=[A-Z])/g, '$1 ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text || /^[a-z]/.test(text)) {return null;}

  const complete = text.match(UP_TO_LAST_SENTENCE_END)?.[0] ?? '';
  const sentences: string[] = [];
  for (const sentence of complete.split(SENTENCE_BOUNDARY)) {
    const key = normalize(sentence);
    if (key && !sentences.some(s => normalize(s) === key)) {
      sentences.push(sentence.trim());
    }
  }
  const cleaned = sentences.join(' ');
  if (!cleaned) {return null;}
  if (previousPosted && normalize(previousPosted) === normalize(cleaned)) {return null;}
  return cleaned;
}
